/**
 * ArchLang v0.10 — Selector & Expression AST (§1–§2 of the views/policies design).
 *
 * Two sub-grammars that meet ONLY at parenthesised keyword portals
 * (`where (…)`, `exists (…)`, `count (…)`, `… of (…)`): a **selector** picks a
 * node set or edge set out of the resolved model + derived graph; an
 * **expression** computes a scalar/render value over a bound subject. They are
 * mutually recursive (`where` carries an expr; `exists`/aggregates carry a
 * selector), so both node families live here.
 *
 * This module is data-only — the parser (selector-parser.ts) builds these
 * nodes, the evaluator (P1b) walks them. Sort (node vs edge) is NOT recorded
 * here: it is a compositional property decided during evaluation (design §1),
 * so the AST stays a pure structural tree. Parens are likewise absent as a
 * node: grouping is captured by the tree shape, and the formatter (P1a-c)
 * re-inserts parens from precedence.
 */

import type { Name, QualifiedName, SourceSpan, Value } from "./types.js";

// =============================================================================
// Selectors
// =============================================================================

/** Arrow operators over the edge universe (design §1 "Edge patterns"). */
export type ArrowOp = ">" | ">>" | "<>";

/** Sort-converting extraction heads (design §1 "Extraction"). */
export type ExtractionHead = "sources" | "targets" | "nodes" | "owners";

/** `within N` distance bound: a literal number or a `$knob` reference. */
export interface WithinBound {
  readonly value: number | { readonly knob: Name };
  readonly source?: SourceSpan;
}

/** `*` — the universal atom (all elements in scope). */
export interface SelUniversal {
  readonly kind: "universal";
  readonly source?: SourceSpan;
}

/** `this` — the bound subject (legal only where a subject exists). */
export interface SelThis {
  readonly kind: "this";
  readonly source?: SourceSpan;
}

/** `outside` — elements not selected in this view (hide/style edge sides). */
export interface SelOutside {
  readonly kind: "outside";
  readonly source?: SourceSpan;
}

/** `$name` — a knob reference (element-kind knob → element atom). */
export interface SelKnob {
  readonly kind: "knob";
  readonly name: Name;
  readonly source?: SourceSpan;
}

/**
 * A bare identifier path: a built-in kind atom (`module`/`interface`/…), a user
 * type name, an element ref, or a `query` ref. Which one it denotes is a
 * resolution concern (P1b homonym rules); the parser only records the path.
 */
export interface SelRef {
  readonly kind: "ref";
  readonly path: QualifiedName;
  readonly source?: SourceSpan;
}

/**
 * A `$knob` in an aspect-atom VALUE slot (`@@key:$knob`) — a value-kind knob
 * reference (design §1 node atoms: value-kind knobs appear in value positions).
 * Shape mirrors {@link WithinBound}'s knob side; the evaluator substitutes the
 * bound value with the literal's own semantics.
 */
export interface AspectValueKnob {
  readonly knob: Name;
  readonly source?: SourceSpan;
}

/**
 * `@@key` / `@@key:"value"` / `@@key:Place` / `@@key:$knob` — an aspect atom.
 * The valued form is a PARSER composition: the `:value` glues to the key with
 * no interior whitespace (design §1). `value` is a string (aspect), identifier
 * (ref), or a `$knob` reference resolved at evaluation.
 */
export interface SelAspect {
  readonly kind: "aspect";
  readonly path: QualifiedName;
  readonly value?: Value | AspectValueKnob;
  readonly source?: SourceSpan;
}

/**
 * `@deco` / `@deco:"v3"` in ATOM position — the CARRIERS of a decoration,
 * the exact parity of the `@@key` aspect atom. The valued form matches on the
 * decoration's PRIMARY field, so `@deprecated:"v3"` reads "deprecated at v3".
 *
 * Position is what separates this from the `@deco.field` getter: an atom sits
 * in node-selector position, a getter in expression position.
 */
export interface SelDecoration {
  readonly kind: "decoration";
  readonly name: Name;
  readonly value?: Value;
  readonly source?: SourceSpan;
}

/** `violating <PolicyRef>` — a sort-carrying set term (sort follows the policy). */
export interface SelViolating {
  readonly kind: "violating";
  readonly policy: QualifiedName;
  readonly source?: SourceSpan;
}

/** `sources of` / `targets of` / `nodes of` / `owners of` `( <selector> )`. */
export interface SelExtraction {
  readonly kind: "extraction";
  readonly head: ExtractionHead;
  readonly inner: Selector;
  readonly source?: SourceSpan;
}

/** One arrow hop: an operator, an optional `within` bound, and the RHS side. */
export interface SelHop {
  readonly op: ArrowOp;
  readonly rhs: Selector;
  readonly within?: WithinBound;
  readonly source?: SourceSpan;
}

/**
 * `X > Y`, `X >> Y within N`, `A > B > C` — an arrow or chain. A chain is a
 * left-associative collection of hops sharing a head; it is a single set term
 * (the desugared hop-union is implicitly parenthesised, design §1). Each side
 * is itself a node-sort selector; an arrow nested inside a side is a diagnostic
 * (enforced by the parser, not the type).
 */
export interface SelArrow {
  readonly kind: "arrow";
  readonly head: Selector;
  readonly hops: readonly SelHop[];
  readonly source?: SourceSpan;
}

/** `in <Module | Space | Process | this> [depth N]` predicate. `depth` bounds
 *  structural containment to N levels below the target (SELECTORS §3). */
export interface SelIn {
  readonly kind: "in";
  readonly arg: Selector;
  readonly depth?: WithinBound;
  readonly source?: SourceSpan;
}

/** `depth <N>` predicate — containment depth from the model root (top-level
 *  module = 1). The anchorless sibling of `in <Ref> depth N`. */
export interface SelDepth {
  readonly kind: "depth";
  readonly bound: WithinBound;
  readonly source?: SourceSpan;
}

/** `on <plane>` predicate (plane names are open-world). */
export interface SelOn {
  readonly kind: "on";
  readonly plane: Name;
  readonly source?: SourceSpan;
}

/** `where ( <expr> )` predicate — parens mandatory. */
export interface SelWhere {
  readonly kind: "where";
  readonly expr: Expr;
  readonly source?: SourceSpan;
}

/** A conjunction of terms (`X and Y and …`). */
export interface SelAnd {
  readonly kind: "and";
  readonly terms: readonly Selector[];
  readonly source?: SourceSpan;
}

/** A disjunction of terms (`X or Y or …`) — same-sort operands (P1b). */
export interface SelOr {
  readonly kind: "or";
  readonly terms: readonly Selector[];
  readonly source?: SourceSpan;
}

/** `not <term>` — complement within the term's sort universe. */
export interface SelNot {
  readonly kind: "not";
  readonly term: Selector;
  readonly source?: SourceSpan;
}

export type Selector =
  | SelUniversal
  | SelThis
  | SelOutside
  | SelKnob
  | SelRef
  | SelAspect
  | SelDecoration
  | SelViolating
  | SelExtraction
  | SelArrow
  | SelIn
  | SelDepth
  | SelOn
  | SelWhere
  | SelAnd
  | SelOr
  | SelNot;

// =============================================================================
// Expressions
// =============================================================================

export type BinaryOp =
  | "="
  | "!="
  | "<"
  | "<="
  | ">"
  | ">="
  | "+"
  | "-"
  | "*"
  | "/"
  | "and"
  | "or";

export type UnaryOp = "not" | "-";

export type AggregateOp = "count" | "count distinct" | "sum" | "min" | "max";

/** `@` = field/builtin getter; `@@` = aspect getter. */
export type GetterSigil = "@" | "@@";

/** A literal — number/string/boolean/identifier (bare idents in exprs are values). */
export interface ExprLiteral {
  readonly kind: "literal";
  readonly value: Value;
  readonly source?: SourceSpan;
}

/** `this` — the bound subject inside a portal/obligation. */
export interface ExprThis {
  readonly kind: "this";
  readonly source?: SourceSpan;
}

/**
 * A sigiled getter: `@version` / `@repo.url` (field), `@@team` (aspect),
 * `@name` (builtin — resolution decides), `@ver.changes(last 90 days)`
 * (capability — free-form args raw-scanned to the first `)`).
 */
export interface ExprGetter {
  readonly kind: "getter";
  readonly sigil: GetterSigil;
  readonly path: QualifiedName;
  /** Raw capability argument text (verbatim, sans parens) when `(…)` followed. */
  readonly capabilityArgs?: string;
  readonly source?: SourceSpan;
}

/** `not X` or unary `-X` (prefix, binds tighter than binary). */
export interface ExprUnary {
  readonly kind: "unary";
  readonly op: UnaryOp;
  readonly operand: Expr;
  readonly source?: SourceSpan;
}

/** A binary operation — comparison, arithmetic, or boolean. */
export interface ExprBinary {
  readonly kind: "binary";
  readonly op: BinaryOp;
  readonly left: Expr;
  readonly right: Expr;
  readonly source?: SourceSpan;
}

/**
 * `count ( <sel> )`, `count distinct @g of ( <sel> )`,
 * `sum|min|max @g of ( <sel> )`. The getter is absent only for plain `count`.
 */
export interface ExprAggregate {
  readonly kind: "aggregate";
  readonly op: AggregateOp;
  readonly getter?: ExprGetter;
  readonly selector: Selector;
  readonly source?: SourceSpan;
}

/** A registered function call — `colorize(v)`, `heat(v, min, max)`, `css(v)`. */
export interface ExprCall {
  readonly kind: "call";
  readonly fn: Name;
  readonly args: readonly Expr[];
  readonly source?: SourceSpan;
}

/** `exists ( <selector> )` — true iff the selector is non-empty under `this`. */
export interface ExprExists {
  readonly kind: "exists";
  readonly selector: Selector;
  readonly source?: SourceSpan;
}

export type Expr =
  | ExprLiteral
  | ExprThis
  | ExprGetter
  | ExprUnary
  | ExprBinary
  | ExprAggregate
  | ExprCall
  | ExprExists;

// =============================================================================
// Reserved vocabularies (design §1 "Reserved at term head", §2 builtins/functions)
// =============================================================================

/**
 * Words reserved at **term-head position** in a selector slot. An element so
 * named needs a qualified path there (spec §4.9 slot reservation). `in`/`on`
 * are lexer keywords; the rest are contextual identifiers.
 */
export const SELECTOR_HEAD_KEYWORDS: ReadonlySet<string> = new Set([
  "and",
  "or",
  "not",
  "in",
  "on",
  "within",
  "depth",
  "where",
  "violating",
  "exists",
  "this",
  "outside",
  "sources",
  "targets",
  "nodes",
  "owners",
  "of",
]);

/** Builtin getter heads — a reserved namespace (design §2). Node + edge. */
export const BUILTIN_GETTERS: ReadonlySet<string> = new Set([
  "name",
  "type",
  "connections",
  "todos",
  "dist",
  "reversible",
  "compensated",
  // process-tests P9 governance — `@tested` (has a passing test) + `@testCoverage`
  // (choice-point coverage %), resolved via the injected ProcessTestResolver.
  "tested",
  "testCoverage",
  "kind",
  // The time-resolved lifecycle state at the view's scrub point:
  // `planned | live | deprecated | retired`. Distinct from the
  // `@planned` / `@deprecated` CARRIER tests — those ask what is written on the
  // element, this asks what it MEANS at a point in time.
  "lifecycle",
  // v0.10.2 latency analysis — a DOTTED builtin: the reservation covers the
  // whole `estimatedLatency.*` subtree (`.min` / `.system` / `.business` /
  // `.bounded` / `.unestimated`). The shadow-lint fires on any field whose
  // top segment is `estimatedLatency`.
  "estimatedLatency",
]);

/** v1 registered expression functions (render casts). Extensible by registration. */
export const EXPRESSION_FUNCTIONS: ReadonlySet<string> = new Set(["colorize", "heat", "css", "distinct"]);

/** The casts that need a value UNIVERSE to assign a color, so they only mean
 *  something inside a view's `style` body — in an element body there is no set
 *  to be relative to, and the cast is rejected. */
export const SET_RELATIVE_FUNCTIONS: ReadonlySet<string> = new Set(["distinct"]);

// =============================================================================
// Rendering an expression back to source
// =============================================================================

/**
 * An expression as source-ish text — for diagnostics, hover, and anything that
 * writes a value back. Not a formatter: it renders the shapes that can appear in
 * a field value (`@@team`, `colorize(@@team)`, literals, arithmetic) and falls
 * back to a placeholder for the query-only shapes, which cannot occur there.
 */
export function exprText(e: Expr): string {
  switch (e.kind) {
    case "literal":
      return valueText(e.value);
    case "this":
      return "this";
    case "getter": {
      const base = `${e.sigil}${e.path.join(".")}`;
      return e.capabilityArgs !== undefined ? `${base}(${e.capabilityArgs})` : base;
    }
    case "unary":
      return e.op === "not" ? `not ${exprText(e.operand)}` : `-${exprText(e.operand)}`;
    case "binary":
      return `${exprText(e.left)} ${e.op} ${exprText(e.right)}`;
    case "call":
      return `${e.fn}(${e.args.map(exprText).join(", ")})`;
    case "aggregate":
      return e.getter ? `${e.op} ${exprText(e.getter)} of (…)` : `${e.op}(…)`;
    case "exists":
      return "exists (…)";
  }
}

/** A {@link Value} as source-ish text. Mirrors `exprText` for literals. */
export function valueText(v: Value): string {
  switch (v.kind) {
    case "string":
      return JSON.stringify(v.text);
    case "identifier":
      return v.text;
    case "ref":
      return v.instance ? `${v.path.join(".")} as ${v.instance}` : v.path.join(".");
    case "number":
      return String(v.value);
    case "boolean":
      return v.value ? "true" : "false";
    case "list":
      return v.items.map(valueText).join(", ");
    case "expr":
      return exprText(v.expr);
  }
}
